import type { MessageView } from "@walkie/protocol"
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef } from "react"
import { agentColor } from "../lib/agents"
import { dayLabel } from "../lib/time"
import { isGithub } from "../lib/messages"
import { loadOlder, markRead, toggleGithub } from "../store/actions"
import { isOpenRequest } from "../store/derived"
import { getState, setUi, useStore, type Filter } from "../store/store"
import { hiddenGithubCount, visibleRoots } from "../store/visible"
import { Composer } from "./Composer"
import { MessageRow } from "./MessageRow"
import { ThreadPane } from "./ThreadPane"

const FILTERS: { id: Filter; label: string; key: string }[] = [
	{ id: "all", label: "All", key: "All messages" },
	{ id: "requests", label: "Requests", key: "Only requests" },
	{ id: "blockers", label: "Blockers", key: "Only blockers" },
	{ id: "mine", label: "Mine", key: "Involving me or my agents" },
	{ id: "failing", label: "Failing", key: "Messages with failed deliveries" },
]

function useVisible(): MessageView[] {
	const messages = useStore(s => s.messages)
	const roomId = useStore(s => s.ui.roomId)
	const filter = useStore(s => s.ui.filter)
	const author = useStore(s => s.ui.authorFilter)
	const agents = useStore(s => s.agents)
	const now = useStore(s => s.now)
	const me = useStore(s => s.me)
	const hideGithub = useStore(s => s.hideGithub)
	return useMemo(
		() => visibleRoots(getState()),
		[messages, roomId, filter, author, agents, now, me, hideGithub],
	)
}

/** "GitHub" chip: hides/shows GitHub-authored messages; shows how many are hidden. */
function GithubToggle() {
	const hide = useStore(s => s.hideGithub)
	const messages = useStore(s => s.messages)
	const roomId = useStore(s => s.ui.roomId)
	const hidden = useMemo(
		() => hiddenGithubCount(getState()),
		[messages, roomId, hide],
	)
	const any = useMemo(
		() =>
			Object.values(messages).some(m => m.roomId === roomId && !m.threadId && isGithub(m)),
		[messages, roomId],
	)
	if (!any && !hide) return null
	const label = hide
		? `GitHub events hidden${hidden ? ` (${hidden})` : ""}; show them`
		: "GitHub events shown; hide them"
	return (
		<button
			className={"gh-chip" + (hide ? " off" : "")}
			aria-pressed={!hide}
			aria-label={label}
			title={`${label} (g h)`}
			onClick={toggleGithub}
		>
			<span className="gh-dot" aria-hidden />
			<span className="gh-label">GitHub</span>
			{hide && hidden > 0 && (
				<span className="gh-n">
					{hidden}
					<span className="gh-n-word"> hidden</span>
				</span>
			)}
		</button>
	)
}

export function Center() {
	const threadId = useStore(s => s.ui.threadId)
	return (
		<main
			className={"center" + (threadId ? " with-thread" : "")}
			aria-label="Conversation"
		>
			<Conversation />
			{threadId && <ThreadPane rootId={threadId} />}
		</main>
	)
}

function Conversation() {
	const roomId = useStore(s => s.ui.roomId)
	const room = useStore(s => (roomId ? s.rooms[roomId] : undefined))
	const selectedId = useStore(s => s.ui.selectedId)
	const filter = useStore(s => s.ui.filter)
	const authorId = useStore(s => s.ui.authorFilter)
	const author = useStore(s => (authorId ? s.agents[authorId] : undefined))
	const hasOlder = useStore(s => s.hasOlder)
	const loading = useStore(s => s.loading)
	const loadError = useStore(s => s.loadError)
	const visible = useVisible()
	const list = useRef<HTMLDivElement>(null)
	const atBottom = useRef(true)
	const openCount = useMemo(() => visible.filter(isOpenRequest).length, [visible])

	// Start at the bottom when the room changes; stay pinned while new messages arrive.
	useLayoutEffect(() => {
		atBottom.current = true
	}, [roomId, filter, authorId])
	useLayoutEffect(() => {
		const el = list.current
		if (el && atBottom.current) el.scrollTop = el.scrollHeight
	}, [visible])

	useEffect(() => {
		if (!selectedId) return
		document.getElementById(`m-${selectedId}`)?.scrollIntoView({ block: "nearest" })
	}, [selectedId])

	useEffect(() => {
		const onVis = () =>
			roomId && document.visibilityState === "visible" && markRead(roomId)
		document.addEventListener("visibilitychange", onVis)
		return () => document.removeEventListener("visibilitychange", onVis)
	}, [roomId])

	const roomLabel = room ? (room.kind === "dm" ? "Direct message" : `#${room.name}`) : "—"

	return (
		<section className="conv" aria-label={roomLabel}>
			<header className="conv-h">
				<span className="conv-title">{roomLabel}</span>
				{room?.topic && <span className="conv-topic ell">{room.topic}</span>}
				{openCount > 0 && <span className="chip">{openCount} open</span>}
				<div className="filters">
					<GithubToggle />
					{author && (
						<span className="to-chip" style={{ color: agentColor(author.color) }}>
							@{author.handle}
							<button
								aria-label="Clear agent filter"
								onClick={() => setUi({ authorFilter: null })}
							>
								×
							</button>
						</span>
					)}
					<div className="seg" role="group" aria-label="Filter messages">
						{FILTERS.map(f => (
							<button
								key={f.id}
								aria-pressed={filter === f.id}
								title={f.key}
								onClick={() => setUi({ filter: f.id, selectedId: null })}
							>
								{f.label}
							</button>
						))}
					</div>
				</div>
			</header>
			{loading && <div className="loading-bar" aria-label="Loading" />}
			<div
				ref={list}
				className="list"
				role="listbox"
				aria-label={`Messages in ${roomLabel}`}
				onScroll={e => {
					const el = e.currentTarget
					atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40
				}}
			>
				{loadError && (
					<div className="empty" role="alert">
						Could not load this project: {loadError}
					</div>
				)}
				{hasOlder && visible.length >= 30 && filter === "all" && (
					<div style={{ textAlign: "center", padding: 4 }}>
						<button className="btn btn-xs btn-ghost" onClick={() => void loadOlder()}>
							Load older
						</button>
					</div>
				)}
				{!loading && !loadError && !visible.length && (
					<div className="empty">
						{filter === "all" && !authorId
							? "Nothing here yet. Say hello below."
							: "No messages match this filter."}
					</div>
				)}
				{visible.map((m, i) => {
					const prev = visible[i - 1]
					const newDay =
						!prev ||
						new Date(prev.createdAt).toDateString() !==
							new Date(m.createdAt).toDateString()
					const cont =
						!newDay &&
						!!prev &&
						prev.author.handle === m.author.handle &&
						(m.kind === "message" || m.kind === "reply") &&
						!m.title &&
						!m.toAgents.length &&
						!m.toUsers.length &&
						m.priority !== "urgent" &&
						Date.parse(m.createdAt) - Date.parse(prev.createdAt) < 5 * 60_000
					return (
						<Fragment key={m.id}>
							{newDay && <div className="day">{dayLabel(m.createdAt)}</div>}
							<MessageRow m={m} selected={m.id === selectedId} cont={cont} />
						</Fragment>
					)
				})}
			</div>
			<Composer
				target="main"
				roomId={roomId}
				placeholder={`Message ${roomLabel} — @ to mention`}
			/>
		</section>
	)
}
